import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useInViewEffect } from 'react-hook-inview';
import { useSelector } from 'react-redux';

import { selectPages } from '../pdf-tool-manage/pdf-slice';
import { Range } from '../store/app-state';

export interface ThumbnailGroupProps {
  range: Range;
  pdf: any;
}

export const ThumbnailGroup: React.FC<ThumbnailGroupProps> = ({
  range,
  pdf,
}) => {
  const isMultiRange = range.fromPage !== range.toPage;
  const [inView, setInView] = useState(false);

  const getSelectPages = useSelector(selectPages);

  const ref = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
      }
      setInView(entry.isIntersecting);
    },
    { threshold: 0.5 },
  );

  useEffect(() => {
    if (!range.fromPage || range.fromPage > pdf.numPages) {
      return;
    }
    const canvas: any = document.getElementById(
      `page-thumbnail-from-${range.name}-${range.fromPage}`,
    );
    const context = canvas.getContext('2d');

    const pageFrom = getSelectPages[range.fromPage];
    if (pageFrom !== undefined && inView) {
      pdf.getPage(range.fromPage).then((page) => {
        const viewport = page.getViewport({ scale: 1 });
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        page.render(renderContext).promise.then(() => {});
      });
    }
  }, [range.fromPage, inView]);

  useEffect(() => {
    if (!range.toPage || range.toPage > pdf.numPages || !isMultiRange) {
      return;
    }
    const canvas: any = document.getElementById(
      `page-thumbnail-to-${range.name}-${range.toPage}`,
    );
    const context = canvas.getContext('2d');

    const pageFrom = getSelectPages[range.toPage];
    if (pageFrom !== undefined && inView) {
      pdf.getPage(range.toPage).then((page) => {
        const viewport = page.getViewport({ scale: 1 });
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };
        page.render(renderContext).promise.then(() => {});
      });
    }
  }, [range.toPage, inView]);

  return (
    <div
      className={classNames('pdf-tool-manage__thumbnail-group-container', {
        'pdf-tool-manage__thumbnail-group-container--small': !isMultiRange,
      })}
    >
      <div className="pdf-tool-manage__thumbnail-group" ref={ref}>
        <div className="pdf-tool-manage__thumbnail-main-container">
          <div className="pdf-tool-manage__thumbnail-left">
            <canvas
              id={`page-thumbnail-from-${range.name}-${range.fromPage}`}
              className="pdf-tool-manage__thumbnail-canvas"
            ></canvas>
          </div>
          {isMultiRange ? (
            <>
              <div className="pdf-tool-manage__thumbnail-space">...</div>
              <div className="pdf-tool-manage__thumbnail-right">
                <canvas
                  id={`page-thumbnail-to-${range.name}-${range.toPage}`}
                  className="pdf-tool-manage__thumbnail-canvas"
                ></canvas>
              </div>
            </>
          ) : null}
        </div>
        <div className="pdf-tool-manage__thumbnail-bottom-row">
          <div className="pdf-tool-manage__thumbnail-left-page">
            {range.fromPage}
          </div>
          <div className="pdf-tool-manage__thumbnail-range-name">
            {range.name}
          </div>
          {isMultiRange ? (
            <div className="pdf-tool-manage__thumbnail-right-page">
              {range.toPage}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
